import React, { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, CalendarDaysIcon, UserIcon, BarChart3Icon } from 'lucide-react';
import { GoalCard } from '../components/GoalCard';
import { PacingChart } from '../components/PacingChart';
import { HistoricalTable } from '../components/HistoricalTable';
import { DailyBreakdown } from '../components/DailyBreakdown';
import { Sidebar } from '../components/Sidebar';
import { Timeframe, getPeriodData } from '../utils/goalData';
type ViewTab = 'current' | 'history';
export function MyGoals() {
  const [timeframe, setTimeframe] = useState<Timeframe>('weekly');
  const [periodOffset, setPeriodOffset] = useState(0);
  const [viewTab, setViewTab] = useState<ViewTab>('current');
  const periodData = getPeriodData(timeframe, periodOffset);
  const isCurrentPeriod = periodOffset === 0;
  function handleTimeframeChange(tf: Timeframe) {
    setTimeframe(tf);
    setPeriodOffset(0);
  }
  return <main className="min-h-screen w-full bg-slate-950" data-id="element-247">
      {/* Top bar with gradient */}
      <header className="border-b border-slate-800 sticky top-0 z-10" style={{
      background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.98) 0%, rgba(2, 6, 23, 0.95) 100%)',
      backdropFilter: 'blur(12px)'
    }} data-id="element-248">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-249">
          <div className="flex items-center justify-between h-16" data-id="element-250">
            <div className="flex items-center gap-3" data-id="element-251">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              boxShadow: '0 0 15px rgba(99, 102, 241, 0.3)'
            }} data-id="element-252">
                <BarChart3Icon className="w-5 h-5 text-white" data-id="element-253" />
              </div>
              <div data-id="element-254">
                <h1 className="text-lg font-bold text-white" data-id="element-255">My Goals</h1>
              </div>
            </div>

            <div className="flex items-center gap-3" data-id="element-256">
              <div className="flex items-center gap-1 bg-slate-800/80 rounded-lg p-1 border border-slate-700/50" data-id="element-257">
                <button onClick={() => setViewTab('current')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${viewTab === 'current' ? 'bg-slate-700 text-white shadow-sm' : 'text-slate-400 hover:text-slate-300'}`} aria-label="Current period view" data-id="element-258">
                  <BarChart3Icon className="w-4 h-4" data-id="element-259" />
                  <span className="hidden sm:inline" data-id="element-260">Current</span>
                </button>
                <button onClick={() => setViewTab('history')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${viewTab === 'history' ? 'bg-slate-700 text-white shadow-sm' : 'text-slate-400 hover:text-slate-300'}`} aria-label="Historical view" data-id="element-261">
                  <CalendarDaysIcon className="w-4 h-4" data-id="element-262" />
                  <span className="hidden sm:inline" data-id="element-263">History</span>
                </button>
              </div>

              <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2), rgba(139, 92, 246, 0.2))',
              border: '1px solid rgba(99, 102, 241, 0.3)'
            }} data-id="element-264">
                <UserIcon className="w-5 h-5 text-indigo-400" data-id="element-265" />
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-266">
        {/* Controls bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6" data-id="element-267">
          {/* Timeframe toggle */}
          <div className="flex items-center gap-3" data-id="element-268">
            <span className="text-sm font-medium text-slate-500" data-id="element-269">
              Timeframe:
            </span>
            <div className="flex items-center gap-1 bg-slate-800/80 border border-slate-700/50 rounded-xl p-1" data-id="element-270">
              <button onClick={() => handleTimeframeChange('weekly')} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${timeframe === 'weekly' ? 'text-white shadow-sm' : 'text-slate-400 hover:text-slate-300'}`} style={timeframe === 'weekly' ? {
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              boxShadow: '0 0 12px rgba(99, 102, 241, 0.3)'
            } : undefined} aria-pressed={timeframe === 'weekly'} data-id="element-271">
                Weekly
              </button>
              <button onClick={() => handleTimeframeChange('monthly')} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${timeframe === 'monthly' ? 'text-white shadow-sm' : 'text-slate-400 hover:text-slate-300'}`} style={timeframe === 'monthly' ? {
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              boxShadow: '0 0 12px rgba(99, 102, 241, 0.3)'
            } : undefined} aria-pressed={timeframe === 'monthly'} data-id="element-272">
                Monthly
              </button>
            </div>
          </div>

          {/* Period navigator */}
          <div className="flex items-center gap-3" data-id="element-273">
            <button onClick={() => setPeriodOffset(periodOffset + 1)} className="w-9 h-9 flex items-center justify-center rounded-lg border border-slate-700/50 bg-slate-800/80 hover:bg-slate-700/80 transition-all duration-300" aria-label="Previous period" data-id="element-274">
              <ChevronLeftIcon className="w-4 h-4 text-slate-400" data-id="element-275" />
            </button>
            <div className="px-4 py-2 bg-slate-800/80 border border-slate-700/50 rounded-lg min-w-[200px] text-center" data-id="element-276">
              <span className="text-sm font-semibold text-slate-200" data-id="element-277">
                {periodData.label}
              </span>
              {isCurrentPeriod && <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2), rgba(139, 92, 246, 0.2))',
              color: '#a5b4fc',
              border: '1px solid rgba(99, 102, 241, 0.3)'
            }} data-id="element-278">
                  CURRENT
                </span>}
            </div>
            <button onClick={() => setPeriodOffset(Math.max(0, periodOffset - 1))} disabled={periodOffset === 0} className="w-9 h-9 flex items-center justify-center rounded-lg border border-slate-700/50 bg-slate-800/80 hover:bg-slate-700/80 transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Next period" data-id="element-279">
              <ChevronRightIcon className="w-4 h-4 text-slate-400" data-id="element-280" />
            </button>
          </div>
        </div>

        {/* Period info bar */}
        {isCurrentPeriod && <div className="bg-slate-800/80 border border-slate-700/50 rounded-xl p-4 mb-6" data-id="element-281">
            <div className="flex flex-wrap items-center gap-6" data-id="element-282">
              <div className="flex items-center gap-2" data-id="element-283">
                <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" style={{
              boxShadow: '0 0 8px rgba(52, 211, 153, 0.5)'
            }} data-id="element-284" />
                <span className="text-sm text-slate-400" data-id="element-285">
                  Day{' '}
                  <span className="font-semibold text-slate-200" data-id="element-286">
                    {periodData.daysPassed}
                  </span>{' '}
                  of {periodData.totalDays}
                </span>
              </div>
              <div className="h-4 w-px bg-slate-700" data-id="element-287" />
              <span className="text-sm text-slate-400" data-id="element-288">
                <span className="font-semibold text-slate-200" data-id="element-289">
                  {periodData.daysRemaining}
                </span>{' '}
                days remaining
              </span>
              <div className="h-4 w-px bg-slate-700" data-id="element-290" />
              <div className="flex-1 max-w-xs" data-id="element-291">
                <div className="h-2 bg-slate-700 rounded-full overflow-hidden" data-id="element-292">
                  <div className="h-full rounded-full transition-all duration-500" style={{
                width: `${periodData.daysPassed / periodData.totalDays * 100}%`,
                background: 'linear-gradient(90deg, #6366f1, #8b5cf6)',
                boxShadow: '0 0 6px rgba(99, 102, 241, 0.4)'
              }} data-id="element-293" />
                </div>
              </div>
              <span className="text-xs font-medium text-slate-500" data-id="element-294">
                {Math.round(periodData.daysPassed / periodData.totalDays * 100)}
                % elapsed
              </span>
            </div>
          </div>}

        {/* Main content with sidebar */}
        <div className="flex gap-6" data-id="element-295">
          {/* Sidebar */}
          <Sidebar periodData={periodData} timeframe={timeframe} data-id="element-296" />

          {/* Main content */}
          <div className="flex-1 min-w-0" data-id="element-297">
            {viewTab === 'current' ? <div className="space-y-6" data-id="element-298">
                {/* Goal Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-299">
                  <GoalCard metric={periodData.softPull} daysPassed={periodData.daysPassed} totalDays={periodData.totalDays} daysRemaining={periodData.daysRemaining} accentColor="#818cf8" accentColorLight="rgba(129, 140, 248, 0.1)" gradientFrom="#6366f1" gradientTo="#a78bfa" glowColor="rgba(99, 102, 241, 0.12)" data-id="element-300" />
                  <GoalCard metric={periodData.submitted} daysPassed={periodData.daysPassed} totalDays={periodData.totalDays} daysRemaining={periodData.daysRemaining} accentColor="#22d3ee" accentColorLight="rgba(34, 211, 238, 0.1)" gradientFrom="#06b6d4" gradientTo="#3b82f6" glowColor="rgba(34, 211, 238, 0.12)" data-id="element-301" />
                </div>

                {/* Pacing Chart */}
                <PacingChart dailyData={periodData.dailyData} daysPassed={periodData.daysPassed} totalDays={periodData.totalDays} data-id="element-302" />

                {/* Historical Table */}
                <HistoricalTable periods={periodData.historicalPeriods} timeframe={timeframe} data-id="element-303" />

                {/* Daily Breakdown - Moved to bottom */}
                {timeframe === 'weekly' && <DailyBreakdown dailyData={periodData.dailyData} daysPassed={periodData.daysPassed} softPullGoal={periodData.softPull} submittedGoal={periodData.submitted} dailyLoans={periodData.dailyLoans} data-id="element-304" />}
              </div> : <div className="space-y-6" data-id="element-305">
                <HistoricalTable periods={periodData.historicalPeriods} timeframe={timeframe} data-id="element-306" />
              </div>}
          </div>
        </div>
      </div>
    </main>;
}